import { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Container,
  PaperProps,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Paper
} from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import { colors } from 'src/components/App/colors';
import { ContentPageTitle } from '../../ContentPageTitle/ContentPageTitle';
import { ActivityTable } from './ActivityTable';
import { PageViewport } from './types';
import { getPageViewportText } from './utils';
import useFetchAllActivitiesInProgess from './useFetchAllActivitiesInProgress';

const StyledPaper = (props: PaperProps) => {
  return (
    <Paper
      elevation={0}
      {...props}
      sx={{
        backgroundColor: '#f8f8f8'
      }}
    />
  );
};

const ActivitiesInProgress = () => {
  const [pageViewport, setPageViewport] = useState<PageViewport>();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));

  const { state } = useLocation<{ deleteId: string; updatedId: string }>();
  const history = useHistory();

  const { deleteId, updatedId } = state || {};

  useEffect(() => {
    // clear out the state after x seconds after we come from quick log to here with either updatedId or deleteId to clear out the alert
    if (!deleteId && !updatedId) {
      return;
    }
    const index = setTimeout(() => {
      history.push('#', { replace: true, state: {} });
    }, 7000);
    return () => {
      clearTimeout(index);
    };
  }, [deleteId, history, updatedId]);

  const [selectedActivityStatus, setSelectedActivityStatus] = useState<string[]>([]);

  const fetchActivities = useFetchAllActivitiesInProgess();
  return (
    <>
      <ContentPageTitle currentPageTitle="Activities In Progress" />
      <Container maxWidth="lg">
        <Box
          flexWrap={'wrap-reverse'}
          mb={2}
          mt={2}
          display={'flex'}
          justifyContent={'space-between'}
          gap={2}
          alignItems={'center'}
        >
          <Box>
            <Typography color={'primary'} variant={'body1'}>
              Results{' '}
            </Typography>
            {pageViewport && (
              <Typography variant={'body2'} color={colors['charcoal.800']}>
                {getPageViewportText(pageViewport)}
              </Typography>
            )}
          </Box>

          {matches && <Box width={'100%'} />}

          <Box display={'flex'} gap={2} flex={{ xs: 1, sm: 0 }}>
            <Autocomplete
              PaperComponent={StyledPaper}
              limitTags={2}
              value={selectedActivityStatus}
              onChange={(_event, value) => {
                setSelectedActivityStatus(value);
              }}
              multiple={true}
              fullWidth
              disablePortal={true}
              options={['Draft', 'Awaiting Approval', 'Returned']}
              disableCloseOnSelect={false}
              renderOption={(props, optionValue) => {
                return (
                  <li {...props}>
                    <Box sx={{ display: 'flex', width: '100%' }}>
                      <Typography variant="body2">{optionValue}</Typography>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField {...params} label="Activity Status" placeholder="Activity Status" />
              )}
              sx={{
                '&.MuiAutocomplete-fullWidth': { maxWidth: { sm: '287px', lg: 'initial' } },
                '& .MuiAutocomplete-tag': { maxWidth: { xs: '100%', sm: '170px' } },
                minWidth: { sm: 270, xs: 0 },
                backgroundColor: 'white'
              }}
            />
          </Box>
        </Box>

        <Paper elevation={2} sx={{ width: '100%', p: { xs: 0, md: 3 } }}>
          <Box pb={{ xs: 3, md: 0 }}>
            <ActivityTable
              items={fetchActivities.allActivities}
              activityStatus={selectedActivityStatus}
              onViewPortChanged={setPageViewport}
              loading={fetchActivities?.loading}
            />
          </Box>
        </Paper>
      </Container>
    </>
  );
};
export { ActivitiesInProgress };
